import { CampaignV2Redirect } from './campaignDefaultVersion';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CreatorAuthProvider } from './context/CreatorAuthContext';
import { BrandAuthProvider } from './context/BrandAuthContext';
import { ToastProvider } from './context/ToastContext';
import { CategoriesProvider } from './context/CategoriesContext';
import LoginPage from './pages/LoginPage';
import { useAppMenu } from './useAppMenu';
import NotificationBell from './components/NotificationBell';
import {
  Users, Megaphone, Building2,
  UserCog, Settings2, LogOut, KeyRound,
  Menu, Sparkles, Activity, Star, Table2, Map, IndianRupee, History,
  ClipboardCheck,
  ShieldCheck, UserX, Ban, TrendingUp,
} from 'lucide-react';

function AccessDenied() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', gap: 10, padding: '40px 24px', textAlign: 'center',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: 'var(--surface2)', color: 'var(--text-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 4,
      }}>🔒</div>
      <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 600 }}>Access restricted</h2>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-2)', maxWidth: 340 }}>
        You don't have permission to view this section. Contact your administrator.
      </p>
    </div>
  );
}

function NoAccess({ user, logout }) {
  const firstName = user?.name ? user.name.trim().split(' ')[0] : null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', gap: 10, padding: '40px 24px', textAlign: 'center',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: 'var(--accent-bg)', color: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 4,
      }}>👋</div>
      <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 600 }}>
        Hi{firstName ? `, ${firstName}` : ''}!
      </h2>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-2)', maxWidth: 360 }}>
        Your account hasn't been given access to any sections yet.
        Ask your administrator to assign you the right permissions.
      </p>
      <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={logout}>
        Sign out
      </button>
    </div>
  );
}

// After a deploy, a tab left open from before still references the old
// chunk hashes — those files are gone (postbuild rsync --delete removes
// stale ones), so the next lazy-loaded route 404s. A hard reload fetches
// the current index.html + chunk hashes and self-heals; guard against a
// reload loop in case it's a genuine, persistent failure instead.
function isChunkLoadError(error) {
  return error?.name === 'ChunkLoadError' || /Loading (chunk|CSS chunk) \d+ failed/.test(error?.message || '');
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    if (isChunkLoadError(error) && !sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', '1');
      window.location.reload();
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', gap: 12, padding: '40px 24px', textAlign: 'center',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'var(--red-bg)', color: 'var(--red)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, marginBottom: 4,
          }}>!</div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--text)', fontWeight: 600 }}>
            Something went wrong
          </h2>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-2)', maxWidth: 380 }}>
            {this.state.error.message || 'An unexpected error occurred on this page.'}
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-secondary" onClick={() => window.location.href = '/'}>
              Go to home
            </button>
            <button className="btn btn-primary" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function IgNavIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}
import './index.css';

const AgenciesPage        = lazy(() => import('./pages/AgenciesPage'));
const AgencyDetailPage    = lazy(() => import('./pages/AgencyDetailPage'));
const CampaignsPage       = lazy(() => import('./pages/CampaignsPage'));
const CampaignCreatePage  = lazy(() => import('./pages/CampaignCreatePage'));
const TeamPage            = lazy(() => import('./pages/TeamPage'));
const SettingsPage        = lazy(() => import('./pages/SettingsPage'));
const IGDiscoverPage      = lazy(() => import('./pages/IGDiscoverPage'));
const PublicFormPage      = lazy(() => import('./pages/PublicFormPage'));
const VendorDetailsPage   = lazy(() => import('./pages/VendorDetailsPage'));
const BillUploadPage      = lazy(() => import('./pages/BillUploadPage'));
const AdCodePage          = lazy(() => import('./pages/AdCodePage'));
const DetailsCheckPage    = lazy(() => import('./pages/DetailsCheckPage'));
const PendingApprovalsPage = lazy(() => import('./pages/PendingApprovalsPage'));
const CreatorEligibilityPage = lazy(() => import('./pages/CreatorEligibilityPage'));
const ViralProfilesPage = lazy(() => import('./pages/ViralProfilesPage'));   // (25 Oct 2026) CEO and COO only
const BlacklistPage = lazy(() => import('./pages/BlacklistPage'));
const ProfileCompletionPage = lazy(() => import('./pages/ProfileCompletionPage'));
// ShipAddressPage (/ship/:token): retired 2026-09-10, back 14 Oct 2026 for barter campaigns (see index.js).
const ShipAddressPage = lazy(() => import('./pages/ShipAddressPage'));
const CreatorLoginPage    = lazy(() => import('./pages/CreatorLoginPage'));
const CreatorPanelLayout  = lazy(() => import('./pages/CreatorPanelPage'));
const BrandLoginPage      = lazy(() => import('./pages/BrandLoginPage'));
const DecisionConfirmPage = lazy(() => import('./pages/DecisionConfirmPage'));
const ChangeApprovalPage  = lazy(() => import('./pages/ChangeApprovalPage'));
const ChangeApprovalsPage = lazy(() => import('./pages/ChangeApprovalsPage'));
const BrandPortalLayout   = lazy(() => import('./pages/BrandPortalPage'));
const ChangePasswordModal    = lazy(() => import('./components/ChangePasswordModal'));
const ReviewerActivityPage   = lazy(() => import('./pages/ReviewerActivityPage'));
const MainDatabasePage       = lazy(() => import('./pages/MainDatabasePage'));
const CampaignEditPage       = lazy(() => import('./pages/CampaignEditPage'));
const CampaignV31Page        = lazy(() => import('./pages/CampaignV31Page'));   // (27 Oct 2026) V3.1: scout and execute on one page; V3 stays as it was   // (20 Oct 2026) the one-sheet campaign page, read only, permission campaigns.sheet_v3
const CampaignSuggestFocusPage = lazy(() => import('./pages/CampaignSuggestFocusPage'));
const ContactViewsPage       = lazy(() => import('./pages/ContactViewsPage'));
const ActivityLogPage        = lazy(() => import('./pages/ActivityLogPage'));
const ApprovalsPage          = lazy(() => import('./pages/ApprovalsPage'));
const CreatorEditPage        = lazy(() => import('./pages/CreatorEditPage'));
const RosterPage             = lazy(() => import('./pages/RosterPage'));
const RosterEntryPage        = lazy(() => import('./pages/RosterEntryPage'));
const TrackerPage            = lazy(() => import('./pages/TrackerPage'));
const RevenuePage            = lazy(() => import('./pages/RevenuePage'));
const RoadmapPage            = lazy(() => import('./pages/RoadmapPage'));
const QuickAddProfilePage    = lazy(() => import('./pages/QuickAddProfilePage'));

function PageLoader() {
  return (
    <div className="page-loader" role="status" aria-label="Loading">
      <div className="spinner" />
    </div>
  );
}

function usePageTitle(hasPerm) {
  const { pathname } = useLocation();
  if (pathname === '/creators/new') return 'Add creator';
  if (/^\/creators\/[^/]+\/edit/.test(pathname)) return 'Edit creator';
  // Users with fill/approve-missing-data perms but not creators.view (e.g.
  // execution_team, account_manager) only ever see the Needs Data tab at
  // this route — the header title should say what they're actually looking at.
  if (pathname === '/') return hasPerm('creators.view') ? 'Creators' : 'Needs Data';
  if (pathname.startsWith('/campaigns/new')) return 'New campaign';
  if (/^\/campaigns\/[^/]+\/edit/.test(pathname)) return 'Edit campaign';
  if (/^\/campaigns-v(2|32)\/[^/]+/.test(pathname)) return 'Campaign';
  if (pathname === '/campaigns') return 'Campaigns';
  if (pathname === '/agencies') return 'Agencies';
  if (pathname === '/ig-discover') return 'IG Discover';
  if (pathname === '/team') return 'Team';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/approvals') return 'Profile data approvals';
  if (pathname === '/change-approvals') return 'Campaign change requests';
  if (pathname === '/pipeline-approvals') return 'Fees and bills to approve';
  if (pathname === '/block-list') return 'Block list';
  if (pathname === '/blacklist') return 'Blacklist';
  if (pathname === '/add-profile') return 'Add profile';
  if (pathname === '/roster' || pathname.startsWith('/roster/')) return 'Roster';
  if (pathname === '/tracker') return 'Tracker';
  if (pathname === '/revenue') return 'Revenue';
  if (pathname === '/contact-views') return 'Contact views';
  if (pathname === '/activity-log') return 'Activity log';
  if (pathname === '/agencies' || pathname.startsWith('/agencies/')) return 'Agencies';
  if (pathname === '/reviewer-activity') return 'Reviewer Watch';
  if (pathname === '/roadmap') return 'Roadmap';
  return 'Creator DB';
}

function SidebarLink({ to, end, icon: Icon, label, badge, alert }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-label={label}
      aria-label={badge ? `${label} — ${badge} need attention` : undefined}
      className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
    >
      <span className="sidebar-link-icon-wrap">
        <Icon size={16} strokeWidth={2} aria-hidden="true" />
      </span>
      <span className="sidebar-link-label">{label}</span>
      {!!badge && <span className={`sidebar-link-badge${alert ? ' is-alert' : ''}`} aria-hidden="true">{badge > 99 ? '99+' : badge}</span>}
    </NavLink>
  );
}

function Sidebar({ user, onChangePwd, logout, hasPerm, isCEO, open }) {
  const canManageUsers = hasPerm('team.manage') || hasPerm('settings.access');
  const initials = user.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  // Numbered badge for Main Database's Needs Data tab — polled here (not
  // derived from MainDatabasePage's own state) so the actual count is
  // visible from the sidebar without having to open the page first, same
  // self-contained fetch+interval pattern as NotificationBell. Only fetched
  // for roles that can actually act on the queue — GET /field-edits/queue
  // itself requires creators.fill_missing_data (see routes/fieldEdits.js).
  const canWorkQueue = hasPerm('creators.fill_missing_data') || hasPerm('creators.approve_missing_data');
  const [needsDataCount, setNeedsDataCount] = useState(0);
  useEffect(() => {
    if (!canWorkQueue) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/field-edits/queue', { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setNeedsDataCount(data.data.length);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canWorkQueue]);

  // Badge for the Approvals inbox — same self-contained poll pattern as
  // needsDataCount above, just a different endpoint/permission. Only
  // ceo/coo/account_manager ever hold creators.approve_missing_data.
  const canApproveQueue = hasPerm('creators.approve_missing_data');
  const [approvalsCount, setApprovalsCount] = useState(0);
  useEffect(() => {
    if (!canApproveQueue) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/field-edits/pending-approvals', { credentials: 'include' });
        const data = await res.json();
        // Count only rows this user can actually act on right now — not
        // rows awaiting a different role (e.g. an AM's own sign-off already
        // recorded, now waiting on CEO/COO) or fields nobody with this
        // account can approve. A badge that counts things you can't do
        // anything about just trains people to ignore it.
        if (!cancelled && data.success) setApprovalsCount(data.data.filter(r => r.can_act).length);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canApproveQueue]);

  // Badge for the CEO change-approval queue (shipment edits after dispatch,
  // contacted-creator removals, product-list changes waiting on the CEO).
  const canDecideChanges = user?.role === 'ceo' || user?.role === 'coo';
  const [changeApprovalCount, setChangeApprovalCount] = useState(0);
  useEffect(() => {
    if (!canDecideChanges) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/change-requests/pending-count', { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setChangeApprovalCount(data.count);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canDecideChanges]);

  // Creators waiting for the CEO's eligibility review (pages/CreatorEligibilityPage.js); the COO sees the count too.
  const [eligibilityCount, setEligibilityCount] = useState(0);
  useEffect(() => {
    if (!canDecideChanges) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/creator-eligibility/count', { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setEligibilityCount(data.waiting);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canDecideChanges]);

  // Blacklist suggestions waiting for the CEO (pages/BlacklistPage.js).
  const [blacklistCount, setBlacklistCount] = useState(0);
  useEffect(() => {
    if (!isCEO) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/creator-blacklist/count', { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setBlacklistCount(data.waiting);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isCEO]);

  // Settings that need the CEO (a mailbox that can't sign in, Instagram not connected, an access key about to stop): a red count on Settings.
  // Same list as Settings' "Needs your attention" part (GET /api/settings/attention, CEO only).
  const [settingsAttention, setSettingsAttention] = useState(0);
  useEffect(() => {
    if (!isCEO) return;
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch('/api/settings/attention', { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setSettingsAttention(data.items.length);
      } catch {}
    }
    poll();
    const interval = setInterval(poll, 5 * 60000);
    window.addEventListener('settings-attention', poll);   // Settings looked again: show the same count here
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener('settings-attention', poll); };
  }, [isCEO]);

  return (
    <aside id="app-menu" className={`app-sidebar${open ? ' open' : ''}`} aria-label="Main navigation" aria-hidden={!open}>

      {/* Brand / logo */}
      <div className="sidebar-logo-area">
        <NavLink to="/" className="sidebar-brand">
          <div className="sidebar-brand-icon">
            <Sparkles size={16} strokeWidth={2.5} />
          </div>
          <div className="sidebar-brand-text">
            <div className="sidebar-brand-name">Creator DB</div>
            <div className="sidebar-brand-sub">Social Infinite</div>
          </div>
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {/* Formerly three entries (Creators, Sheet View, Main Database) —
            consolidated 2026-09-02 into one page with an All Creators /
            Needs Data / Sheet View tab bar. Label reflects what the user
            actually lands on: execution_team/account_manager have
            fill/approve perms but not creators.view, so they only ever see
            the Needs Data tab at this route. */}
        {/* Core screens first, then the approval queues, then admin. Creators / Needs Data: execution_team and account_manager have fill/approve perms but not creators.view, so they only see the Needs Data tab at this route. */}
        {(hasPerm('creators.view') || canWorkQueue) &&
          <SidebarLink to="/" end icon={Users} label={hasPerm('creators.view') ? 'Creators' : 'Needs Data'} badge={needsDataCount} />}
        {hasPerm('campaigns.view') && <SidebarLink to="/campaigns" icon={Megaphone} label="Campaigns" />}
        {hasPerm('agencies.view') && <SidebarLink to="/agencies" icon={Building2} label="Agencies" />}
        <SidebarLink to="/roster" icon={Star} label="Roster" />
        {user?.role !== 'reviewer' && <SidebarLink to="/tracker" icon={Table2} label="Tracker" />}
        {hasPerm('ig.discover') && <SidebarLink to="/ig-discover" icon={IgNavIcon} label="IG Discover" />}
        {canDecideChanges && <SidebarLink to="/viral-profiles" icon={TrendingUp} label="Viral profiles" />}
        {user?.email === 'puneetjainwork@gmail.com' && <SidebarLink to="/reviewer-activity" icon={Activity} label="Reviewer Watch" />}
        {user?.email === 'puneetjainwork@gmail.com' && <SidebarLink to="/roadmap" icon={Map} label="Roadmap" />}

        {(canApproveQueue || user?.role !== 'reviewer' || isCEO) && (
          <>
            <p className="sidebar-section-label">Approvals</p>
            {canApproveQueue && <SidebarLink to="/approvals" icon={ClipboardCheck} label="Profile data approvals" badge={approvalsCount} />}
            {user?.role !== 'reviewer' && <SidebarLink to="/change-approvals" icon={ShieldCheck} label="Campaign change requests" badge={changeApprovalCount} />}
            {isCEO && <SidebarLink to="/pipeline-approvals" icon={ClipboardCheck} label="Fees and bills to approve" />}
            {user?.role !== 'reviewer' && <SidebarLink to="/blacklist" icon={Ban} label="Blacklist" badge={isCEO ? blacklistCount : 0} />}
            {canDecideChanges && <SidebarLink to="/block-list" icon={UserX} label="Block list" badge={eligibilityCount} />}
          </>
        )}

        {canManageUsers && (
          <>
            <p className="sidebar-section-label">Manage</p>
            {hasPerm('team.manage')     && <SidebarLink to="/team"     icon={UserCog}   label="Team" />}
            {(hasPerm('settings.access') || hasPerm('brands.manage')) && <SidebarLink to="/settings" icon={Settings2} label="Settings" badge={isCEO ? settingsAttention : 0} alert />}
          </>
        )}

        {isCEO && (
          <>
            <p className="sidebar-section-label">Reports</p>
            <SidebarLink to="/revenue" icon={IndianRupee} label="Revenue" />
            <SidebarLink to="/contact-views" icon={Activity} label="Contact views" />
            <SidebarLink to="/activity-log" icon={History} label="Activity log" />
            <a
              href="/health-dashboard"
              target="_blank"
              rel="noopener noreferrer"
              data-label="Health"
              className="sidebar-link"
            >
              <Activity size={16} strokeWidth={2} aria-hidden="true" />
              <span className="sidebar-link-label">Health</span>
            </a>
          </>
        )}
      </nav>

      {/* Bottom: user card + collapse button */}
      <div className="sidebar-bottom">
        <div className="sidebar-user-card">
          <div className="sidebar-user-av">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user.name}</div>
            {isCEO && <div className="sidebar-user-role">{user.roleLabel}</div>}
          </div>
          <div className="sidebar-user-actions">
            <button
              className="sidebar-user-btn"
              onClick={onChangePwd}
              title="Change password"
              aria-label="Change password"
            >
              <KeyRound size={13} />
            </button>
            <button
              className="sidebar-user-btn danger"
              onClick={logout}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>

      </div>
    </aside>
  );
}

function ProtectedLayout() {
  const { user, loading, logout, hasPerm, isCEO } = useAuth();
  const pageTitle = usePageTitle(hasPerm);
  const navigate  = useNavigate();
  const [showChangePwd, setShowChangePwd] = useState(false);

  // Keyboard arrow key horizontal scrolling — tracks whichever .table-scroll
  // the mouse is currently over and scrolls it on left/right arrow keys.
  useEffect(() => {
    let hoveredTable = null;
    const STEP = 120;

    function onEnter(e) {
      const t = e.target.closest('.table-scroll');
      if (t) hoveredTable = t;
    }
    function onLeave(e) {
      if (hoveredTable && !hoveredTable.contains(e.relatedTarget)) hoveredTable = null;
    }
    function onKey(e) {
      if (!hoveredTable) return;
      if (e.target.closest('input,select,textarea,[contenteditable]')) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); hoveredTable.scrollLeft -= STEP; }
      if (e.key === 'ArrowRight') { e.preventDefault(); hoveredTable.scrollLeft += STEP; }
    }

    document.addEventListener('mouseover',  onEnter);
    document.addEventListener('mouseout',   onLeave);
    document.addEventListener('keydown',    onKey);
    return () => {
      document.removeEventListener('mouseover',  onEnter);
      document.removeEventListener('mouseout',   onLeave);
      document.removeEventListener('keydown',    onKey);
    };
  }, []);

  // Drag-to-scroll for all .table-scroll containers — lets users without a
  // horizontal-scroll mouse drag the table sideways with a click-and-drag.
  useEffect(() => {
    let el = null, startX = 0, scrollLeft = 0, dragging = false;

    function onMouseDown(e) {
      const target = e.target.closest('.table-scroll');
      if (!target) return;
      // Don't steal clicks on interactive elements
      if (e.target.closest('a,button,input,select,textarea,[contenteditable]')) return;
      // A press on the box's own scrollbar is the scrollbar's, not a drag (long tables now have a vertical one)
      if (e.target === target && (e.offsetX > target.clientWidth || e.offsetY > target.clientHeight)) return;
      el = target;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      dragging = false;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    }
    function onMouseMove(e) {
      if (!el) return;
      const dx = e.pageX - el.offsetLeft - startX;
      if (!dragging && Math.abs(dx) < 4) return;
      dragging = true;
      e.preventDefault();
      el.scrollLeft = scrollLeft - dx;
    }
    function onMouseUp() {
      if (!el) return;
      el.style.cursor = '';
      el.style.userSelect = '';
      el = null;
    }

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  const { isPhone, menuOpen, deskOpen, phoneOpen, setPhoneOpen, toggleMenu } = useAppMenu();

  if (loading) {
    return (
      <div className="app-loading" role="status" aria-label="Loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className={`app${!isPhone && deskOpen ? ' menu-pushed' : ''}`}>
      <Sidebar
        user={user}
        onChangePwd={() => setShowChangePwd(true)}
        logout={logout}
        hasPerm={hasPerm}
        isCEO={isCEO}
        open={menuOpen}
      />
      {isPhone && phoneOpen ? <div className="menu-backdrop" onClick={() => setPhoneOpen(false)} aria-hidden="true" /> : null}

      <div className="app-main">
        <header className="header">
          <div className="header-inner">
            <button type="button" className="menu-burger" onClick={toggleMenu} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="app-menu">
              <Menu size={20} strokeWidth={2.25} />
            </button>
            <div className="header-page-title">{pageTitle}</div>
            <div className="header-actions">
              <NotificationBell userId={user.id} />
            </div>
          </div>
        </header>

        <div className="content-area">
          <RouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* "Creators" — formerly Main Database (/main-database), now
                  the single creator-listing entry point at "/". Old Creators
                  page and Sheet View (/database-v2) were folded in as tabs;
                  see MainDatabasePage.js. */}
              <Route path="/" element={
                (hasPerm('creators.view') || hasPerm('creators.fill_missing_data') || hasPerm('creators.approve_missing_data')) ? <MainDatabasePage /> :
                hasPerm('campaigns.view') ? <Navigate to="/campaigns" replace /> :
                hasPerm('agencies.view')  ? <Navigate to="/agencies" replace /> :
                hasPerm('ig.discover')    ? <Navigate to="/ig-discover" replace /> :
                <NoAccess user={user} logout={logout} />
              } />
              <Route path="/creators/new"        element={hasPerm('creators.add')    ? <CreatorEditPage />    : <Navigate to="/" replace />} />
              <Route path="/creators/:id/edit"   element={hasPerm('creators.edit')   ? <CreatorEditPage />    : <Navigate to="/" replace />} />
              {/* Old bookmarks/links for the pages folded into "/" still land somewhere useful. */}
              <Route path="/database-v2"         element={<Navigate to="/" replace />} />
              <Route path="/main-database"       element={<Navigate to="/" replace />} />
              <Route path="/approvals"           element={hasPerm('creators.approve_missing_data') ? <ApprovalsPage /> : <AccessDenied />} />
              <Route path="/change-approvals"    element={user?.role === 'reviewer' ? <AccessDenied /> : <ChangeApprovalsPage />} />
              <Route path="/add-profile"         element={hasPerm('creators.quickadd') ? <QuickAddProfilePage /> : <AccessDenied />} />
              <Route path="/agencies"            element={hasPerm('agencies.view')   ? <AgenciesPage />       : <AccessDenied />} />
              <Route path="/agencies/:id"        element={hasPerm('agencies.view')   ? <AgencyDetailPage />   : <AccessDenied />} />
              <Route path="/campaigns"           element={hasPerm('campaigns.view')  ? <CampaignsPage />      : <AccessDenied />} />
              <Route path="/campaigns/new"       element={hasPerm('campaigns.create')? <CampaignCreatePage /> : <Navigate to="/campaigns" replace />} />
              <Route path="/campaigns/:id/edit"  element={hasPerm('campaigns.edit')  ? <CampaignEditPage />   : <Navigate to="/campaigns" replace />} />
              <Route path="/campaigns-v2/:id" element={<CampaignV2Redirect />} />   {/* (30 Oct 2026) Version 3.2 is the default campaign page */}
              <Route path="/campaigns-v32/:id"   element={hasPerm('campaigns.sheet_v3') ? <CampaignV31Page />  : <Navigate to="/campaigns" replace />} />   {/* (29 Oct 2026) V3.2: the same page, with deliverable cells on the Execute tab; same permission */}
              {/* Needs Data folded into this page as a tab (2026-07-27) — old bookmarks/links still land somewhere useful. */}
              <Route path="/needs-data"          element={<Navigate to="/" replace />} />
              <Route path="/roster"      element={<RosterPage />} />
              <Route path="/roster/:id"  element={<RosterEntryPage />} />
              <Route path="/tracker"     element={user?.role === 'reviewer' ? <Navigate to="/" replace /> : <TrackerPage />} />
              <Route path="/revenue"     element={isCEO ? <RevenuePage /> : <AccessDenied />} />
              <Route path="/pipeline-approvals" element={isCEO ? <PendingApprovalsPage /> : <AccessDenied />} />
              <Route path="/block-list" element={['ceo', 'coo'].includes(user?.role) ? <CreatorEligibilityPage /> : <AccessDenied />} />
              <Route path="/viral-profiles" element={['ceo', 'coo'].includes(user?.role) ? <ViralProfilesPage /> : <AccessDenied />} />
              <Route path="/creator-eligibility" element={<Navigate to="/block-list" replace />} />
              <Route path="/blacklist" element={user?.role === 'reviewer' ? <AccessDenied /> : <BlacklistPage />} />
              <Route path="/contact-views" element={isCEO ? <ContactViewsPage /> : <AccessDenied />} />
              <Route path="/activity-log" element={isCEO ? <ActivityLogPage /> : <AccessDenied />} />
              <Route path="/ig-discover" element={hasPerm('ig.discover') ? <IGDiscoverPage onNavigateToSettings={() => navigate('/settings?section=instagram')} /> : <Navigate to="/" replace />} />
              <Route path="/reviewer-activity" element={user?.email === 'puneetjainwork@gmail.com' ? <ReviewerActivityPage /> : <Navigate to="/" replace />} />
              <Route path="/roadmap"           element={user?.email === 'puneetjainwork@gmail.com' ? <RoadmapPage />         : <Navigate to="/" replace />} />
              <Route path="/team"        element={hasPerm('team.manage')     ? <TeamPage />     : <Navigate to="/" replace />} />
              <Route path="/settings"    element={(hasPerm('settings.access') || hasPerm('brands.manage')) ? <SettingsPage /> : <Navigate to="/" replace />} />
              <Route path="*"            element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </div>
      </div>

      {showChangePwd && (
        <Suspense fallback={null}>
          <ChangePasswordModal onClose={() => setShowChangePwd(false)} />
        </Suspense>
      )}
    </div>
  );
}

// Browser-tab titles of the pages people outside the company see (creators, brands, vendors). They never say "Creator Database". The vendor and bill pages set their own.
const PUBLIC_TITLES = [
  ['/form/', 'Campaign Response'], ['/ship/', 'Shipping Address'], ['/roster-update/', 'Update Your Profile'], ['/confirm/', 'Confirm Your Response'],
  ['/approve-change/', 'Approve a Change'], ['/creator-login', 'Creator Panel'], ['/creator-panel', 'Creator Panel'],
  ['/brand-login', 'Brand Portal'], ['/brand-portal', 'Brand Portal'], ['/login', 'Sign in'],
];
function PublicTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const hit = PUBLIC_TITLES.find(([prefix]) => pathname.startsWith(prefix));
    if (hit) document.title = `${hit[1]} | Social Infinite`;
  }, [pathname]);
  return null;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PublicTitle />
        <ToastProvider>
          <Suspense fallback={<div className="app-loading" role="status" aria-label="Loading"><div className="spinner" /></div>}>
            <Routes>
              <Route path="/login"       element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/form/:token" element={<PublicFormPage />} />
              <Route path="/ship/:token" element={<ShipAddressPage />} />
              <Route path="/vendor-details" element={<VendorDetailsPage />} />
              <Route path="/bill-upload" element={<BillUploadPage />} />
              <Route path="/ad-code" element={<AdCodePage />} />
              <Route path="/details-check" element={<DetailsCheckPage />} />
              <Route path="/roster-update/:token" element={<ProfileCompletionPage />} />
              <Route path="/confirm/:token" element={<DecisionConfirmPage />} />
              <Route path="/approve-change/:token" element={<ChangeApprovalPage />} />
              <Route path="/creator-login" element={<CreatorAuthProvider><CreatorLoginPage /></CreatorAuthProvider>} />
              <Route path="/creator-panel/*" element={<CreatorAuthProvider><CreatorPanelLayout /></CreatorAuthProvider>} />
              <Route path="/brand-login" element={<BrandAuthProvider><BrandLoginPage /></BrandAuthProvider>} />
              <Route path="/brand-portal/*" element={<BrandAuthProvider><BrandPortalLayout /></BrandAuthProvider>} />
              <Route path="/campaigns-v2/:id/suggest" element={<CampaignSuggestFocusPage />} />
              <Route path="/*"           element={<CategoriesProvider><ProtectedLayout /></CategoriesProvider>} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
